import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useWatch } from 'react-hook-form'

import cx from 'classnames'

import {
  OnlineOrderingSpiCreatePaymentIntentSuccessResponse,
  GetClientTokenQuery,
  useGetClientTokenQuery,
  OnlineOrderingSpiGetClientTokenSuccessResponse
} from 'src/apollo/onlineOrdering'
import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import { _quoteFontFamily } from 'src/shared/js/fontUtils'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useComponentTimeout } from 'shared/js/hooks/useComponentTimeout'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'

import { getSiteStyles } from 'public/components/default_template/meta/StyleMeta'
import { useCart } from 'public/components/online_ordering/CartContext'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'

import PaymentIntentError from './PaymentIntentError'
import { ToastCashPaymentToggle } from './ToastCashPaymentToggle'
import { useSpiEventTracker, useSpiSdk } from './useSpi'
import { t } from 'src/translations'

const SPI_IFRAME_ANCHOR_ID = 'spi-frame-anchor'
const IFRAME_ALERT_TIMEOUT_SECS = 5

const spiReady = (
  spiSdk: any,
  restaurantGuid: string,
  tokenData?: GetClientTokenQuery,
  paymentIntent?: OnlineOrderingSpiCreatePaymentIntentSuccessResponse
) =>
  spiSdk &&
  restaurantGuid &&
  tokenData?.oo.spiGetClientToken.__typename ===
    'OnlineOrderingSpiGetClientTokenSuccessResponse' &&
  paymentIntent?.sessionSecret

type Props = {
  anchorClassName?: string
  allowToastCash?: boolean
  disableSPI?: boolean
  canPayAtRestaurant?: boolean
}

const getSpiStyles = (siteStyles: ReturnType<typeof getSiteStyles>) => {
  return {
    color: '#252525',
    backgroundColor: 'white',
    iconColor: siteStyles.whiteContrastingColor,
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: '18px',
    letterSpacing: 'normal',
    fontFamily: `${_quoteFontFamily(
      siteStyles.bodyFont.fontFamily
    )}, Effra, sans-serif`
  }
}

const CreditCardFormSPI = ({
  anchorClassName,
  allowToastCash,
  disableSPI,
  canPayAtRestaurant
}: Props) => {
  const spiSdk = useSpiSdk()
  const { trackSpi } = useSpiEventTracker()
  const { ooEntpFreedompayEcommEnabled } = useFlags()
  const [loadingForm, setLoadingForm] = useState(true)
  const { restaurant, ooRestaurant } = useRestaurant()
  const { restaurantGuid } = useCart()
  const { customer } = useCustomer()
  const {
    setPaymentOption,
    paymentOption,
    setIsPaymentValid,
    paymentIntent,
    paymentIntentFailure,
    setUserInfoRequired,
    setPaymentType,
    isPaymentValid
  } = usePayment()
  const { data: tokenData, error: tokenError } = useGetClientTokenQuery()

  const firstName = useWatch({ name: 'yourInfoFirstName' })
  const lastName = useWatch({ name: 'yourInfoLastName' })

  const nameOnCard = useMemo(() => {
    // Feature flag check - early return if disabled
    if (!ooEntpFreedompayEcommEnabled) {
      return undefined
    }

    const customerName =
      customer?.firstName && customer?.lastName
        ? `${customer.firstName} ${customer.lastName}`.trim()
        : undefined
    const formName =
      firstName && lastName ? `${firstName} ${lastName}`.trim() : undefined

    if (customer) {
      if (formName && customerName && formName !== customerName) {
        return formName
      }
      if (customerName) {
        return customerName
      }
    }

    return formName || undefined
  }, [ooEntpFreedompayEcommEnabled, customer, firstName, lastName])

  const previousNameOnCardRef = useRef<string | undefined>(undefined)
  const isInitializedRef = useRef(false)

  const { setIsLoaded } = useComponentTimeout(
    'SPI CC form',
    IFRAME_ALERT_TIMEOUT_SECS,
    spiReady(spiSdk, restaurantGuid, tokenData, paymentIntent),
    {
      restaurantName: ooRestaurant?.name,
      paymentIntentId: paymentIntent?.id
    }
  )

  useEffect(() => {
    trackSpi('temp_SPI_loaded', {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (spiSdk) {
      trackSpi('temp_SPI_sdk_loaded', {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spiSdk])

  useEffect(() => {
    if (
      tokenData?.oo.spiGetClientToken.__typename ===
      'OnlineOrderingSpiGetClientTokenSuccessResponse'
    ) {
      trackSpi('temp_SPI_client_token', {})
    } else if (
      tokenData?.oo.spiGetClientToken.__typename ===
      'OnlineOrderingPaymentIntentError'
    ) {
      trackSpi('temp_SPI_client_token_failed', {})
      reportErrorMessageWithData('Failed to create SPI client token', {
        rxGuid: restaurantGuid
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenData])

  useEffect(() => {
    if (tokenError) {
      trackSpi('temp_SPI_client_token_failed', { message: tokenError.message })
      reportErrorMessageWithData('Failed to create SPI client token', {
        rxGuid: restaurantGuid,
        message: tokenError.message
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenError])

  useEffect(() => {
    if (!loadingForm) {
      trackSpi('temp_SPI_sdk_initialized', {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingForm])

  const spiStatusCallback = useCallback(
    (ev: any) => {
      if (paymentOption !== PaymentOption.UponReceipt) {
        setPaymentOption(PaymentOption.PayNow)
      }
      if (ev?.content) {
        setIsPaymentValid(ev.content.isValid)
        setPaymentType(ev.content.selectedPaymentMethod)
        setUserInfoRequired(
          ev.content.selectedPaymentMethod !== 'APPLE_PAY' &&
            ev.content.selectedPaymentMethod !== 'GOOGLE_PAY'
        )
      }
    },
    [
      setPaymentOption,
      setIsPaymentValid,
      setPaymentType,
      setUserInfoRequired,
      paymentOption
    ]
  )

  useEffect(() => {
    let frame: HTMLIFrameElement | undefined
    if (
      !isInitializedRef.current &&
      spiReady(spiSdk, restaurantGuid, tokenData, paymentIntent)
    ) {
      const tokenResponse = tokenData!.oo
        .spiGetClientToken as OnlineOrderingSpiGetClientTokenSuccessResponse

      frame = spiSdk.initialize(
        (ev: any) => {
          setIsLoaded(true)
          setLoadingForm(false)
          spiStatusCallback(ev)
          spiSdk.monitor(spiStatusCallback)
        },
        (err: any) => {
          reportErrorMessageWithData('Failed to initialize SPI SDK', {
            rxGuid: restaurantGuid,
            message: err?.cause?.message,
            developerMessage: err?.cause?.developerMessage
          })
          trackSpi('temp_SPI_sdk_failed', {})
        },
        {
          oauthToken: tokenResponse.token,
          domElementId: SPI_IFRAME_ANCHOR_ID,
          merchantId: restaurantGuid,
          sessionSecret: paymentIntent!.sessionSecret,
          acceptAmex: ooRestaurant?.creditCardConfig.amexAccepted ?? false,
          zipRequired: true,
          nameOnCard: nameOnCard
        },
        getSpiStyles(getSiteStyles(restaurant.meta))
      )

      if (frame) {
        frame.title = t('spi-container.payment-iframe-title')
      }
      isInitializedRef.current = true
      previousNameOnCardRef.current = nameOnCard
    }

    return () => {
      try {
        frame && document.removeChild(frame)
      } catch (e) {
        // ignore
      }
      isInitializedRef.current = false
    }
    // nameOnCard is intentionally omitted - initialization happens once, updates via separate useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spiSdk,
    restaurantGuid,
    ooRestaurant?.creditCardConfig.amexAccepted,
    tokenData,
    paymentIntent,
    spiStatusCallback,
    setIsLoaded,
    restaurant.meta,
    trackSpi
  ])

  useEffect(() => {
    if (
      spiSdk &&
      !loadingForm &&
      nameOnCard !== undefined &&
      nameOnCard !== previousNameOnCardRef.current &&
      typeof spiSdk.updateNameOnCard === 'function'
    ) {
      try {
        spiSdk.updateNameOnCard(nameOnCard)
        previousNameOnCardRef.current = nameOnCard
      } catch (error) {
        console.log(`Error while updating name on card with error: ${error}`)
      }
    }
  }, [spiSdk, nameOnCard, loadingForm])

  useEffect(() => {
    if (isPaymentValid) {
      document
        .getElementById('creditCardFormSpiContainer')
        ?.classList.remove('invalid')
    }
  }, [isPaymentValid])

  return (
    <div className='creditCardFormSpiContainer' id='creditCardFormSpiContainer'>
      <div className='creditCardFormSpi'>
        {allowToastCash ? (
          <ToastCashPaymentToggle
            wrapperStyle={getSpiStyles(getSiteStyles(restaurant.meta))}
          />
        ) : null}
        <div className='disabledWrapper'>
          <div
            id={SPI_IFRAME_ANCHOR_ID}
            className={anchorClassName}
            aria-disabled={disableSPI}
          >
            {/* The form can only finish loading once an intent exists, so a
                failed intent means this shimmer would otherwise run forever. */}
            {loadingForm &&
              (paymentIntentFailure ? (
                <PaymentIntentError
                  failure={paymentIntentFailure}
                  canPayAtRestaurant={canPayAtRestaurant}
                />
              ) : (
                <Skeleton width='100%' height='218px' />
              ))}
          </div>
          <div className={cx('disabledOverlay', { enabled: disableSPI })}></div>
        </div>
      </div>
      <div className='errorMessage hidden' role='alert'>
        {t('spi-container.payment-information-invalid')}
      </div>
    </div>
  )
}

export default CreditCardFormSPI
